import { type ReactNode } from 'react';
import { Link, Outlet, useMatches } from 'react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface BreadcrumbItem {
  label: ReactNode;
  href?: string;
}

interface BreadcrumbHandle {
  breadcrumbs?:
    | BreadcrumbItem[]
    | ((match: { params: Record<string, string> }) => BreadcrumbItem[]);
}

interface AdminLayoutProps {
  className?: string;
}

export const AdminLayout = ({ className }: AdminLayoutProps) => {
  const matches = useMatches();

  const breadcrumbs = matches.flatMap((m) => {
    const handle = m.handle as BreadcrumbHandle | undefined;
    if (!handle?.breadcrumbs) return [];
    return typeof handle.breadcrumbs === 'function'
      ? handle.breadcrumbs(m as { params: Record<string, string> })
      : handle.breadcrumbs;
  });

  const hasBreadcrumbs = breadcrumbs.length > 0;

  return (
    <div className={cn('', className)}>
      {/* Main Content Area */}
      <div className="h-full overflow-auto p-6">
        {hasBreadcrumbs && (
          <div className="flex items-center gap-2 min-h-[36px] mb-4">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 flex-shrink-0"
              asChild
            >
              <Link
                to={breadcrumbs[0]?.href ?? '/app/admin'}
                aria-label="Go back"
              >
                <ChevronLeft className="h-4 w-4" />
              </Link>
            </Button>

            <nav className="flex items-center gap-1.5 text-sm text-muted-foreground truncate">
              {breadcrumbs.map((crumb, index) => (
                <span
                  key={crumb.href ?? String(crumb.label)}
                  className="flex items-center gap-1.5"
                >
                  {index > 0 && (
                    <ChevronRight className="h-3.5 w-3.5 flex-shrink-0" />
                  )}
                  {crumb.href ? (
                    <Link
                      to={crumb.href}
                      className="hover:text-foreground transition-colors"
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-foreground font-medium">
                      {crumb.label}
                    </span>
                  )}
                </span>
              ))}
            </nav>
          </div>
        )}
        <Outlet />
      </div>
    </div>
  );
};
